import React from "react"
import {RefreshCw} from 'lucide-react'
import type { SiteSetting } from "./siteSettingsTypes"

export default function SiteSettingsNewebpayTab(props: {
  newebpayEnabled: boolean
  setNewebpayEnabled: (v: boolean) => void
  newebpayMerchantId: string
  setNewebpayMerchantId: (v: string) => void
  newebpayHashKey: string
  setNewebpayHashKey: (v: string) => void
  newebpayHashIv: string
  setNewebpayHashIv: (v: string) => void
  newebpayTestMode: boolean
  setNewebpayTestMode: (v: boolean) => void
  creditCardOnly: boolean
  setCreditCardOnly: (v: boolean) => void
  initializing: boolean
  onInitFromEnv: () => void
  currentSetting: SiteSetting | undefined
}) {
  const {
    newebpayEnabled,
    setNewebpayEnabled,
    newebpayMerchantId,
    setNewebpayMerchantId,
    newebpayHashKey,
    setNewebpayHashKey,
    newebpayHashIv,
    setNewebpayHashIv,
    newebpayTestMode,
    setNewebpayTestMode,
    creditCardOnly,
    setCreditCardOnly,
    initializing,
    onInitFromEnv
  } = props

  return (
    <div className="mb-8">
      <div className="bg-gray-950 border border-gray-800 p-6">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="flex-1">
            <h3 className="text-xl font-light tracking-wide text-gray-100 mb-2">藍新金流（NewebPay）商家設定</h3>
            <p className="text-sm text-gray-400 mb-3">
              設定藍新金流支付服務的商家資訊，啟用後結帳頁面將使用藍新金流處理付款
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => setNewebpayEnabled(!newebpayEnabled)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                newebpayEnabled ? "bg-green-600" : "bg-gray-700"
              }`}
              aria-label="切換藍新金流"
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  newebpayEnabled ? "translate-x-9" : "translate-x-1"
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${newebpayEnabled ? "text-green-400" : "text-gray-500"}`}>
              {newebpayEnabled ? "已啟用" : "已停用"}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6 space-y-6">
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              商店代號 (Merchant ID) <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={newebpayMerchantId}
              onChange={(e) => setNewebpayMerchantId(e.target.value)}
              placeholder="請輸入藍新金流商店代號"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">
              HashKey <span className="text-red-400">*</span>
            </label>
            <input
              type="password"
              value={newebpayHashKey}
              onChange={(e) => setNewebpayHashKey(e.target.value)}
              placeholder="請輸入 HashKey"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">
              HashIV <span className="text-red-400">*</span>
            </label>
            <input
              type="password"
              value={newebpayHashIv}
              onChange={(e) => setNewebpayHashIv(e.target.value)}
              placeholder="請輸入 HashIV"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="border-t border-gray-800 pt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={newebpayTestMode}
                onChange={(e) => setNewebpayTestMode(e.target.checked)}
                className="w-5 h-5 bg-gray-900 border border-gray-700 checked:bg-blue-600 checked:border-blue-600 cursor-pointer"
              />
              <div>
                <div className="text-gray-100 font-medium">測試模式</div>
                <div className="text-xs text-gray-500">啟用後將使用測試環境（不會真的扣款）</div>
              </div>
            </label>
          </div>

          <div className="border-t border-gray-800 pt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={creditCardOnly}
                onChange={(e) => setCreditCardOnly(e.target.checked)}
                className="w-5 h-5 bg-gray-900 border border-gray-700 checked:bg-blue-600 checked:border-blue-600 cursor-pointer"
              />
              <div>
                <div className="text-gray-100 font-medium">只顯示信用卡付款</div>
                <div className="text-xs text-gray-500">啟用後結帳頁面將只顯示信用卡付款選項</div>
              </div>
            </label>
          </div>

          <div className="border-t border-gray-800 pt-6">
            <div className="bg-purple-950 bg-opacity-30 border border-purple-800 p-4 rounded">
              <div className="text-xs text-purple-300 mb-3">
                如已配置環境變量（NEWEBPAY_MERCHANT_ID、NEWEBPAY_HASH_KEY、NEWEBPAY_HASH_IV），可自動初始化。
              </div>
              <button
                onClick={onInitFromEnv}
                disabled={initializing}
                className="w-full px-4 py-3 bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {initializing ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    <span>初始化中...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    <span>從環境變量初始化設定</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
